import React, { useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useEscapeKey } from '../../../hooks/useKeyboardNavigation';
import { useFocusTrap } from '../../../hooks/useFocusTrap';
import './ModalRoot.css';

// public/index.html mount point (src/index.tsx getElementById('root')).
const APP_ROOT_ID = 'root';

interface ModalRootProps {
  onClose: () => void;
  ariaLabel?: string;
  labelledBy?: string;
  initialFocusRef?: React.RefObject<HTMLElement | null>;
  overlayClassName: string;
  dialogClassName: string;
  children: React.ReactNode;
  'data-testid'?: string;
}

export function ModalRoot({
  onClose,
  ariaLabel,
  labelledBy,
  initialFocusRef,
  overlayClassName,
  dialogClassName,
  children,
  'data-testid': dataTestId
}: ModalRootProps) {
  const openerRef = useRef<HTMLElement | null>(
    typeof document === 'undefined' ? null : document.activeElement as HTMLElement | null
  );
  const dialogRef = useFocusTrap({
    isActive: true,
    initialFocusRef,
    restoreFocus: openerRef.current
  });
  useEscapeKey(onClose);

  // The overlay is portalled outside #root and #root goes inert, so neither Tab nor the
  // TV's spatial navigation can reach the page behind it. A layout cleanup runs before
  // useFocusTrap's passive cleanup, so #root is focusable again by the time focus
  // returns to the opener (focus() on an inert element is a no-op).
  useLayoutEffect(() => {
    const appRoot = document.getElementById(APP_ROOT_ID);
    if (!appRoot) {
      return;
    }

    appRoot.inert = true;
    return () => {
      appRoot.inert = false;
    };
  }, []);

  const overlay = (
    <div
      className={overlayClassName}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          event.preventDefault();
          onClose();
        }
      }}
    >
      <div
        ref={dialogRef}
        className={dialogClassName}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        aria-labelledby={labelledBy}
        data-testid={dataTestId}
      >
        {children}
      </div>
    </div>
  );

  return createPortal(overlay, document.body);
}
